import React, {useEffect, useMemo, useState} from 'react';
import FileListEntry from "../FileListEntry";
import FileGridEntry from "../FileGridEntry";
import {Box, Stack} from "@mui/material";
import {Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {EmptyView} from "@dreebit-registry/vsm-microservice-components";
import _ from "lodash";
import FileGalleryLightbox from "../FileGalleryLightbox";
import {isImageFile} from "../../utils/fileType";
import {FileListViewMode} from "../../utils/fileListViewMode";

import {NodeImageTarget} from "../../hooks/useNodeImage";

export type FileListItem = {
    /** Node-file entity id (InstalledBaseNodeFile / ProductNodeFile). */
    id: string;
    /** File-service UUID of the underlying file — the value `imageId` expects. */
    fileId?: string | null;
    filename: string;
    basename: string;
    extension: string | null;
    comment: string | null;
    supportsComment: boolean;
    category: string | null;
    insert: string | null;
    contentType: string;
    size: number;
    link: string;
    thumbnailLink?: string | null;
    previewLink?: string | null;
    language?: string | null;
    access: { read: boolean; write: boolean; delete: boolean };
    kind: 'installedBaseNode' | 'assetLocation';
};

interface Props {
    files: FileListItem[];
    category?: string;
    userLanguage?: string;
    viewMode: FileListViewMode;
    /** Fixed height for the scroll container. Defaults to the legacy
     *  viewport-derived height used by the asset/system detail pages. */
    height?: number | string;
    /** Enables "use as image" on image files of this node (see useNodeImage). */
    nodeImageTarget?: NodeImageTarget;
}

const FileList = ({files, category, userLanguage, viewMode, height, nodeImageTarget}: Props) => {
    const {t } = useTranslation();

    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryIndex, setGalleryIndex] = useState(0);

    const userFilesCategory = t("User files");
    const uncategorizedCategory = t("Uncategorized");

    // Sort + group files exactly as they are displayed in the list.
    const sortedGroupEntries = useMemo(() => {
        const sortedFiles = _.sortBy(files, [
            file => file.language !== userLanguage,
            file => file.filename?.toLowerCase()
        ]);
        const groupedFiles = _.groupBy(sortedFiles, file => file.category || uncategorizedCategory);

        return Object.entries(groupedFiles).sort((a, b) => {
            const categoryA = a[0];
            const categoryB = b[0];

            if (categoryA === userFilesCategory) return -1;
            if (categoryB === userFilesCategory) return 1;

            if (categoryA === uncategorizedCategory) return 1;
            if (categoryB === uncategorizedCategory) return -1;

            return categoryA.localeCompare(categoryB);
        });
    }, [files, userLanguage, userFilesCategory, uncategorizedCategory]);

    // Flat, display-ordered list so the gallery walks images in list order.
    const orderedFiles = useMemo(
        () => sortedGroupEntries.flatMap(([, groupFiles]) => groupFiles),
        [sortedGroupEntries]
    );

    // Only images appear in the gallery / bottom thumbnail strip.
    const imageFiles = useMemo(
        () => orderedFiles.filter((file) => isImageFile(file.contentType)),
        [orderedFiles]
    );

    const handleOpenFile = (file: FileListItem) => {
        if (isImageFile(file.contentType)) {
            const idx = imageFiles.findIndex((img) => img.id === file.id);
            setGalleryIndex(idx < 0 ? 0 : idx);
            setGalleryOpen(true);
        } else {
            window.open(file.link, "_blank", "noopener,noreferrer");
        }
    };

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const tableHeight =
        viewportHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - 56 // Filter SearchBar
        - 63 // header
        - 50 // Tabs
        - 75 // content padding
        - 53 // chips
    useEffect(() => {
        if (height != null) return; // caller controls the height — no viewport tracking
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [height]);

    return (
        <Stack spacing={2} sx={{height: height ?? tableHeight, overflow: 'hidden'}}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
                {files.length > 0 ? (
                    <Box sx={{flex: 1, overflowY: 'auto', minHeight: 0 }}>
                        {sortedGroupEntries.map(([groupName, groupFiles]) => (
                            <Box key={groupName} sx={{ mb: 2 }}>

                                {/* Gruppen-Überschrift (Sticky) */}
                                <Box sx={{pl:'5px'}} >
                                    <Typography level="titleSmall">
                                        {groupName}
                                    </Typography>
                                </Box>

                                {/* Dateien innerhalb der Gruppe */}
                                {viewMode === 'grid' ? (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                            gap: '16px',
                                            p: '8px',
                                        }}
                                    >
                                        {groupFiles.map((file) => (
                                            <FileGridEntry key={file.id} file={file} onOpen={handleOpenFile} nodeImageTarget={nodeImageTarget} />
                                        ))}
                                    </Box>
                                ) : (
                                    groupFiles.map((file) => (
                                        <FileListEntry key={file.id} file={file} onOpen={handleOpenFile} nodeImageTarget={nodeImageTarget} />
                                    ))
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <EmptyView />
                    </Box>
                )}
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography level={"titleSmallLight"} sx={{pl: "8px"}}>
                    {t("Total")}: {files.length} {t("Files")}
                </Typography>
            </Box>

            <FileGalleryLightbox
                images={imageFiles}
                index={galleryIndex}
                open={galleryOpen}
                onClose={() => setGalleryOpen(false)}
            />

        </Stack>
    );
}

export default FileList;
